import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { insertRecord } from '../../utils/supabase-operations';
import { useGlobalMetadata } from '../../utils/providers';
import { supabaseClient } from '../../utils/supabase';
import { normalizeState, parseProhibitedStates } from '../../utils/states';
import { normalizeIndustry, industryBlocked } from '../../utils/industries';
import { isValidEmail } from '../../utils/validation';
import { getRecentRecords, pushRecentRecord, recentRankMap } from '../../utils/recentRecords';
import { getGoogleAccessToken, useGoogleDriveToken } from '../../features/docs/googleToken';
import useAuth from '../../utils/useAuth';
import { DRIVE_FOLDER_MIME_TYPE, canonicalDriveFolderUrl, drivePreviewUrl, type DriveFile } from '../../features/docs/drive';
import { useFileViewer } from '../FileViewer';
import { useDriveFiles, useMerchantDriveFolder } from '../../features/docs/useDriveFiles';
import { useDebounce } from '@uidotdev/usehooks';
import {
  captureWorkflowException,
  captureWorkflowWarning,
  traceWorkflow,
  workflowBreadcrumb,
} from '../../utils/telemetry';
import { normalizeSubmissionEmailBody, SUBMISSION_NUMBER_PLACEHOLDER } from '../../../../supabase/functions/_shared/submissionEmailBody';
import { requestReportGridRefresh } from '../../utils/reportGridRefresh';
import './SubmissionComposer.css';

/* ── Constants ────────────────────────────────────────── */

// Fallbacks when picklist metadata hasn't loaded yet. Canonical values come
// from core_crm picklists (submission.deal_type / status / structure).
const FALLBACK_DEAL_TYPES = ['New Deal', 'Renewal', 'Stack', 'Add On', 'LOC', 'Reverse Consolidation', 'Buyout', 'In House', 'Flex', 'Side by Side', 'Hop'];
const FALLBACK_STATUSES = ['Submitted', 'Approved', 'Funded', 'Declined', 'Missing documents', 'Contracts Out', 'Contracts In', 'No Response', 'Waiting On'];
const FALLBACK_STRUCTURES = ['Weekly', 'Daily', 'Monthly', 'Bi-Weekly', 'Other'];

const STATUS_SHORT_LABELS: Record<string, string> = {
  'Missing documents': 'Missing Doc',
};

const STATUS_TINTS: Record<string, { bg: string; fg: string; bd: string }> = {
  submitted:            { bg: '#e6ecfb', fg: '#1d4ed8', bd: '#c8d5f3' },
  approved:             { bg: '#e6f4ec', fg: '#0f7a44', bd: '#bfe5cd' },
  funded:               { bg: '#1f2328', fg: '#fff',    bd: '#1f2328' },
  declined:             { bg: '#fdeaea', fg: '#c93b3b', bd: '#f3c4c4' },
  'missing documents':  { bg: '#fdf6e3', fg: '#c98a2a', bd: '#f0dba4' },
  'contracts out':      { bg: '#fff',    fg: '#c98a2a', bd: '#f0dba4' },
  'contracts in':       { bg: '#fff',    fg: '#0f7a44', bd: '#bfe5cd' },
};
const DEFAULT_TINT = { bg: '#f1f3f6', fg: '#6b7280', bd: '#e3e6eb' };

function statusTint(status: string) {
  return STATUS_TINTS[status.toLowerCase()] || DEFAULT_TINT;
}
function statusLabel(status: string) {
  return STATUS_SHORT_LABELS[status] || status;
}

// Files that should start unchecked in the email document list.
const UNCHECKED_BY_DEFAULT = /voided|driver|licen|\bdl\b/i;

interface ComposerRow {
  key: string;
  dealType: string;
  funder: string;
  funderLabel: string;
  status: string;
  amount: string;
  factor: string;
  term: string;
  commPct: string;
  approvalDate: string;
  structure: string;
  declineReason: string;
  missingDocs: string;
  expanded: boolean;
}

interface RecipientPreview {
  funderId: number;
  funderName: string | null;
  toEmails: string[];
  ccEmails: string[];
  /** REG-902: Region funders are sent as Region from deals@regioncapitalgroup.com. */
  brand?: 'Skyline' | 'Region';
  fromEmail?: string;
  error?: string;
}

let _seq = 0;
function freshRow(): ComposerRow {
  return {
    key: `sc-${++_seq}-${Date.now()}`,
    dealType: 'New Deal',
    funder: '',
    funderLabel: '',
    status: 'Submitted',
    amount: '',
    factor: '',
    term: '',
    commPct: '',
    approvalDate: '',
    structure: 'Weekly',
    declineReason: '',
    missingDocs: '',
    expanded: false,
  };
}

/* ── Helpers ────────────────────────────────────────── */

function parseMoney(s: string): number {
  if (!s) return 0;
  const n = parseFloat(s.replace(/[^0-9.]/g, ''));
  return isNaN(n) ? 0 : n;
}
function parseFactor(s: string): number {
  const n = parseFloat(s || '0');
  return isNaN(n) ? 0 : n;
}
function parseTerm(s: string): number {
  if (!s) return 0;
  const m = s.match(/\d+/);
  return m ? parseInt(m[0], 10) : 0;
}
function fmtMoney(n: number): string {
  if (!n) return '';
  return '$' + Math.round(n).toLocaleString('en-US');
}
function fmtSize(bytes: number): string {
  if (!bytes) return '';
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
  return Math.max(1, Math.round(bytes / 1024)) + ' KB';
}
function defaultSubject(merchantLabel: string): string {
  // "NEW DEAL {BUSINESS NAME}" — all caps, no colon/comma.
  const name = merchantLabel.replace(/[,:]/g, '').replace(/\s+/g, ' ').trim().toUpperCase();
  return name ? `NEW DEAL ${name}` : '';
}

/** Real failure reason from a Functions invoke error. supabase-js wraps every
 * non-2xx response in a generic "non-2xx status code" FunctionsHttpError; the
 * actionable message the function returned is in the response body. */
async function functionErrorMessage(err: unknown): Promise<string> {
  const httpError = err as { context?: { json?: () => Promise<{ error?: string }> }; message?: string };
  try {
    const body = await httpError?.context?.json?.();
    if (body?.error) return String(body.error);
  } catch {
    // body not JSON or already consumed — fall through to the generic message
  }
  return httpError?.message || 'unknown error';
}

function parseCcEmails(value: string): { emails: string[]; invalid: string[] } {
  const entries = value.split(/[,;\s]+/).map(email => email.trim()).filter(Boolean);
  const invalid = entries.filter(email => !isValidEmail(email));
  const seen = new Set<string>();
  const emails = entries.filter(email => {
    if (!isValidEmail(email)) return false;
    const key = email.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return { emails, invalid };
}

/* ── Props ──────────────────────────────────────────── */

interface SubmissionComposerProps {
  /** Pre-selected merchant ID (0 = show picker) */
  merchantId?: number | string;
  merchantName?: string;
}

/* ── Server Search Select (callback-driven) ─────────── */

export interface SearchItem {
  id: string;
  label: string;
  disabled?: boolean;
  /** Short annotation shown next to the label (reason, hint). */
  note?: string;
}

interface ServerSearchSelectProps {
  value: string;
  displayValue: string;
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  minWidth?: number;
  /** Runs the search; caller owns matching, ranking, and annotations. */
  onSearch: (term: string) => Promise<SearchItem[]>;
  /** Shown before the user types (e.g. recently viewed). */
  emptyItems?: SearchItem[];
  emptyItemsLabel?: string;
  onChange: (id: string, label: string) => void;
}

function ServerSearchSelect({
  value, displayValue, placeholder = 'Search…', className = '',
  triggerClassName = '', minWidth = 200,
  onSearch, emptyItems, emptyItemsLabel = 'Recent', onChange,
}: ServerSearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<SearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [dropPos, setDropPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearch = useDebounce(search, 250);

  useEffect(() => {
    const term = debouncedSearch.trim();
    if (!term) {
      setResults([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const items = await onSearch(term);
        if (!cancelled) setResults(items);
      } catch (err) {
        console.error('Search failed:', err);
        if (!cancelled) setResults([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [debouncedSearch, onSearch]);

  const openDropdown = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, minWidth) });
    }
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [minWidth]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || dropRef.current?.contains(target)) return;
      setOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = () => { setOpen(false); setSearch(''); };
    window.addEventListener('resize', close);
    const scrollParent = triggerRef.current?.closest('.sky-composer-rows');
    scrollParent?.addEventListener('scroll', close);
    return () => {
      window.removeEventListener('resize', close);
      scrollParent?.removeEventListener('scroll', close);
    };
  }, [open]);

  const handleSelect = useCallback((item: SearchItem) => {
    onChange(item.id, item.label);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('', '');
    setSearch('');
  }, [onChange]);

  return (
    <div className={`sky-searchsel ${className}`} ref={triggerRef}>
      <div
        className={`${triggerClassName || 'sky-composer-input'} sky-searchsel-trigger ${open ? 'sky-searchsel-trigger--open' : ''}`}
        onClick={() => open ? (setOpen(false), setSearch('')) : openDropdown()}
      >
        <span className={`sky-searchsel-display ${!value ? 'sky-searchsel-display--empty' : ''}`}>
          {displayValue || placeholder}
        </span>
        {value && (
          <button className="sky-searchsel-clear" onClick={handleClear} title="Clear">×</button>
        )}
        <svg className="sky-searchsel-chevron" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </div>
      {open && dropPos && ReactDOM.createPortal(
        <div
          ref={dropRef}
          className="sky-searchsel-dropdown"
          style={{ top: dropPos.top, left: dropPos.left, width: dropPos.width }}
        >
          <div className="sky-searchsel-search-wrap">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4-4" />
            </svg>
            <input
              ref={inputRef}
              className="sky-searchsel-search"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`Type to search…`}
              autoFocus
            />
            {loading && <span className="sky-searchsel-spinner" />}
          </div>
          <div className="sky-searchsel-list">
            {!search.trim() && (emptyItems?.length ? (
              <>
                <div className="sky-searchsel-group">{emptyItemsLabel}</div>
                {emptyItems.map(o => (
                  <div
                    key={o.id}
                    className={[
                      'sky-searchsel-option',
                      o.id === value ? 'sky-searchsel-option--active' : '',
                      o.disabled ? 'sky-searchsel-option--disabled' : '',
                    ].join(' ')}
                    onClick={() => { if (!o.disabled) handleSelect(o); }}
                  >
                    {o.label}{o.note ? <span className="sky-searchsel-note"> — {o.note}</span> : null}
                  </div>
                ))}
              </>
            ) : (
              <div className="sky-searchsel-empty">Start typing to search…</div>
            ))}
            {search.trim() && results.map(o => (
              <div
                key={o.id}
                className={[
                  'sky-searchsel-option',
                  o.id === value ? 'sky-searchsel-option--active' : '',
                  o.disabled ? 'sky-searchsel-option--disabled' : '',
                ].join(' ')}
                onClick={() => { if (!o.disabled) handleSelect(o); }}
              >
                {o.label}{o.note ? <span className="sky-searchsel-note"> — {o.note}</span> : null}
              </div>
            ))}
            {!loading && results.length === 0 && search.trim() && (
              <div className="sky-searchsel-empty">No matches</div>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}

/* ── Small labeled field ─────────────────────────────── */

interface FieldProps {
  label: string;
  value: string;
  onChange?: (v: string) => void;
  placeholder?: string;
  options?: string[];
  optionLabels?: Record<string, string>;
  readOnly?: boolean;
  mono?: boolean;
  type?: string;
  inputStyle?: React.CSSProperties;
}

function Field({ label, value, onChange, placeholder, options, optionLabels, readOnly, mono, type, inputStyle }: FieldProps) {
  return (
    <label className="sky-composer-field">
      <span className="sky-composer-field__label">{label}</span>
      {options ? (
        <select
          value={value}
          onChange={e => onChange?.(e.target.value)}
          className="sky-composer-input sky-composer-input--select"
          style={inputStyle}
        >
          {options.map(o => <option key={o} value={o}>{optionLabels?.[o] || o}</option>)}
        </select>
      ) : (
        <input
          value={value}
          onChange={e => onChange?.(e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          type={type || 'text'}
          className={[
            'sky-composer-input',
            mono ? 'sky-composer-input--mono' : '',
            readOnly ? 'sky-composer-input--readonly' : '',
          ].join(' ')}
          style={inputStyle}
        />
      )}
    </label>
  );
}

/* ── Status pill ─────────────────────────────────────── */

function StatusPill({ status }: { status: string }) {
  const t = statusTint(status);
  return (
    <span className="sky-composer-pill" style={{ background: t.bg, color: t.fg, borderColor: t.bd }}>
      <span className="sky-composer-pill__dot" style={{ background: t.fg === '#fff' ? '#4ade80' : t.fg }} />
      {statusLabel(status)}
    </span>
  );
}

/* ── Email panel (right column) ───────────────────────── */

interface EmailPanelProps {
  subject: string;
  setSubject: (s: string) => void;
  checked: Record<string, boolean>;
  setChecked: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  files: DriveFile[];
  filesLoading: boolean;
  filesError: string | null;
  hasDriveFolder: boolean;
  /** Canonical Drive folder URL from merchant.drive_folder, for open-in-new-tab. */
  driveFolderUrl: string | null;
  providerToken: string | null;
  onConnectGoogle: () => void;
}

function EmailPanel({
  subject, setSubject, checked, setChecked,
  files, filesLoading, filesError, hasDriveFolder, driveFolderUrl, providerToken, onConnectGoogle,
}: EmailPanelProps) {
  const { openFile } = useFileViewer();
  const selected = files.filter(f => checked[f.id]);
  const totalBytes = selected.reduce((s, f) => s + Number(f.size || 0), 0);
  const allOn = files.length > 0 && selected.length === files.length;

  const toggle = (id: string) => setChecked(c => ({ ...c, [id]: !c[id] }));
  const toggleAll = () => setChecked(allOn ? {} : files.reduce((a, f) => ((a[f.id] = true), a), {} as Record<string, boolean>));

  return (
    <div className="sky-composer-email">
      <div className="sky-composer-email__head">
        <span className="sky-composer-email__icon">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
            <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" />
          </svg>
        </span>
        <span className="sky-composer-email__title">Submission Email</span>
        <span className="sky-composer-email__hint">· sent to funders on Submitted rows</span>
      </div>

      <label className="sky-composer-field">
        <span className="sky-composer-field__label">Subject</span>
        <input
          value={subject}
          onChange={e => setSubject(e.target.value)}
          className="sky-composer-input sky-composer-email__subject"
        />
      </label>

      <div className="sky-composer-docs">
        <div className="sky-composer-docs__head">
          <span className="sky-composer-field__label">Documents</span>
          {(() => {
            const driveIcon = (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24">
                  <path d="M6 2.5 1.6 10l2.2 3.8L8.2 6.3z" fill="#00ac47" />
                  <path d="M8.2 6.3 12 13h8.4L16.6 6.3z" fill="#ffba00" />
                  <path d="m1.6 10 2.2 3.8h8.5L9.9 10z" fill="#2684fc" />
                </svg>
                <span className="sky-composer-email__hint">Drive folder</span>
              </>
            );
            return driveFolderUrl ? (
              <a
                className="sky-composer-docs__drivelink"
                href={driveFolderUrl}
                target="_blank"
                rel="noreferrer"
                title="Open the merchant's Drive folder"
              >
                {driveIcon}
              </a>
            ) : driveIcon;
          })()}
          <span style={{ flex: 1 }} />
          {files.length > 0 && (
            <button className="sky-composer-docs__selectall" onClick={toggleAll}>
              {allOn ? 'Deselect all' : 'Select all'}
            </button>
          )}
        </div>

        {!hasDriveFolder && !filesLoading ? (
          <div className="sky-composer-docs__empty">No Google Drive folder on this merchant.</div>
        ) : !providerToken ? (
          <div className="sky-composer-docs__empty">
            Google Drive access expired for this session.
            <button className="sky-composer-docs__connect" onClick={onConnectGoogle}>Reconnect Google</button>
          </div>
        ) : filesError ? (
          <div className="sky-composer-docs__empty">
            {filesError}
            <button className="sky-composer-docs__connect" onClick={onConnectGoogle}>Reconnect Google</button>
          </div>
        ) : filesLoading ? (
          <div className="sky-composer-docs__empty">Loading Drive files…</div>
        ) : files.length === 0 ? (
          <div className="sky-composer-docs__empty">Drive folder is empty.</div>
        ) : (
          <div className="sky-composer-docs__list">
            {files.map(f => (
              <div key={f.id} className={`sky-composer-doc ${checked[f.id] ? 'sky-composer-doc--on' : ''}`}>
                {/* checkbox area selects; the rest of the row previews */}
                <label className="sky-composer-doc__check" onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    checked={!!checked[f.id]}
                    onChange={() => toggle(f.id)}
                  />
                </label>
                <button
                  className="sky-composer-doc__open"
                  title={`Preview ${f.name}`}
                  onClick={() => openFile({ directUrl: drivePreviewUrl(f), filename: f.name, mimeType: f.mimeType ?? undefined })}
                >
                  <img src={f.iconLink} alt="" width={16} height={16} onError={e => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }} />
                  <span className="sky-composer-doc__name">{f.name}</span>
                  {f.size && <span className="sky-composer-doc__size">{fmtSize(Number(f.size))}</span>}
                </button>
              </div>
            ))}
          </div>
        )}

        {files.length > 0 && (
          <div className="sky-composer-docs__summary">
            <b>{selected.length}</b> of {files.length} selected{totalBytes > 0 && <> · <b>{fmtSize(totalBytes)}</b></>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Confirm & submit dialog ─────────────────────────── */

interface ConfirmDialogProps {
  merchantLabel: string;
  rows: ComposerRow[];
  subject: string;
  setSubject: (s: string) => void;
  emailBodies: Record<string, string>;
  setEmailBody: (funderId: string, value: string) => void;
  recipientPreview: Record<string, RecipientPreview>;
  recipientPreviewLoading: boolean;
  recipientPreviewError: string | null;
  additionalCc: Record<string, string>;
  setAdditionalCc: (funderId: string, value: string) => void;
  selectedDocs: DriveFile[];
  /** Unchecks the document so it is dropped from the email (chip ×). */
  onRemoveDoc: (id: string) => void;
  sendEmail: boolean;
  setSendEmail: (v: boolean) => void;
  submitting: boolean;
  onBack: () => void;
  onConfirm: () => void;
}

export function ConfirmDialog({
  merchantLabel, rows, subject, setSubject, emailBodies, setEmailBody,
  recipientPreview, recipientPreviewLoading, recipientPreviewError,
  additionalCc, setAdditionalCc, selectedDocs, onRemoveDoc,
  sendEmail, setSendEmail, submitting, onBack, onConfirm,
}: ConfirmDialogProps) {
  const [editOpen, setEditOpen] = useState<Record<string, boolean>>({});
  const saveBody = (funderId: string) => {
    const body = emailBodies[funderId] ?? SUBMISSION_NUMBER_PLACEHOLDER;
    const normalized = normalizeSubmissionEmailBody(body);
    setEmailBody(funderId, normalized);
    if (!body.replace(/\r\n?/g, '\n').split('\n').some(line => line.trim() === SUBMISSION_NUMBER_PLACEHOLDER)) {
      toast.info('The required Sub # line was added back to the start of the body.');
    }
  };
  const saveBodies = () => rows.filter(row => row.status === 'Submitted').forEach(row => saveBody(row.funder));
  const goBack = () => { saveBodies(); onBack(); };
  const [ccOpen, setCcOpen] = useState<Record<string, boolean>>({});
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') goBack(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const emailRows = rows.filter(r => r.status === 'Submitted');
  const willEmail = sendEmail && emailRows.length > 0;
  const invalidCc = emailRows.flatMap(row => parseCcEmails(additionalCc[row.funder] ?? '').invalid);
  const routeErrors = emailRows
    .map(row => recipientPreview[row.funder]?.error)
    .filter((error): error is string => !!error);
  const previewUnavailable = willEmail && (recipientPreviewLoading || !!recipientPreviewError || routeErrors.length > 0);
  const confirmDisabled = submitting || previewUnavailable || (willEmail && invalidCc.length > 0);

  return ReactDOM.createPortal(
    <div className="sky-composer-confirm__overlay" onClick={goBack}>
      <div className="sky-composer-confirm" onClick={e => e.stopPropagation()}>
        <div className="sky-composer-confirm__body">
          <div className="sky-composer-confirm__title">
            Submit <span>{merchantLabel.toUpperCase()}</span>?
          </div>

          <span className="sky-composer-field__label">
            {rows.length} submission{rows.length > 1 ? 's' : ''}
          </span>
          <div className="sky-composer-confirm__deals">
            {rows.map(r => {
              const preview = recipientPreview[r.funder];
              const ccValue = additionalCc[r.funder] ?? '';
              const ccInvalid = parseCcEmails(ccValue).invalid;
              const showRoute = sendEmail && r.status === 'Submitted';
              return (
                <div key={r.key} className="sky-composer-confirm__dealwrap">
                  <div className="sky-composer-confirm__deal">
                    <span className="sky-composer-confirm__funder">{r.funderLabel}</span>
                    {showRoute && preview?.brand === 'Region' && (
                      <span
                        className="sky-composer-confirm__brand"
                        title={`Sent as Region${preview.fromEmail ? ` from ${preview.fromEmail}` : ''}. No Skyline address or rep CC.`}
                      >
                        Region
                      </span>
                    )}
                    {showRoute && (
                      <span className={`sky-composer-confirm__recipient ${preview?.error || recipientPreviewError ? 'sky-composer-confirm__recipient--error' : ''}`}>
                        {recipientPreviewLoading
                          ? 'resolving email…'
                          : preview?.error
                            ? preview.error
                            : preview?.toEmails.length
                              ? `— ${preview.toEmails.join(', ')}`
                              : recipientPreviewError || 'recipient unavailable'}
                      </span>
                    )}
                    {showRoute && !recipientPreviewLoading && preview && !preview.error && (
                      <button
                        type="button"
                        className="sky-composer-confirm__ccbutton"
                        onClick={() => setCcOpen(open => ({ ...open, [r.funder]: !open[r.funder] }))}
                      >
                        {ccOpen[r.funder] ? 'Done' : 'CC Email'}
                      </button>
                    )}
                    {r.amount && <span className="sky-composer-confirm__amount">{fmtMoney(parseMoney(r.amount)) || r.amount}</span>}
                    <StatusPill status={r.status} />
                  </div>
                  {showRoute && ccOpen[r.funder] && preview && !preview.error && (
                    <div className="sky-composer-confirm__cceditor">
                      {preview.fromEmail && (
                        <div className="sky-composer-confirm__existingcc">
                          <b>From</b> {preview.fromEmail}
                        </div>
                      )}
                      {preview.ccEmails.length > 0 && (
                        <div className="sky-composer-confirm__existingcc">
                          <b>Already CC</b> {preview.ccEmails.join(', ')}
                        </div>
                      )}
                      <label className="sky-composer-field">
                        <span className="sky-composer-field__label">Additional CC emails</span>
                        <input
                          value={ccValue}
                          onChange={event => setAdditionalCc(r.funder, event.target.value)}
                          placeholder="name@example.com, other@example.com"
                          aria-label={`Additional CC emails for ${r.funderLabel}`}
                          className={`sky-composer-input ${ccInvalid.length ? 'sky-composer-input--error' : ''}`}
                        />
                      </label>
                      {ccInvalid.length > 0 && (
                        <div className="sky-composer-confirm__ccerror">
                          Enter valid email addresses separated by commas.
                        </div>
                      )}
                    </div>
                  )}
                  {showRoute && (
                    <div className="sky-composer-confirm__emailinfo" role="group" aria-label={`Email for ${r.funderLabel}`}>
                      <div className="sky-composer-confirm__subjectrow">
                        <span className="sky-composer-confirm__subject"><b>Subject</b> {subject}</span>
                        <button
                          className="sky-composer-confirm__editlink"
                          onClick={() => { if (editOpen[r.funder]) saveBody(r.funder); setEditOpen(open => ({ ...open, [r.funder]: !open[r.funder] })); }}
                          title="Edit subject & body"
                          aria-label={`${editOpen[r.funder] ? 'Done editing' : 'Edit email for'} ${r.funderLabel}`}
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                          </svg>
                          {editOpen[r.funder] ? 'Done' : 'Edit'}
                        </button>
                      </div>
                      <div className="sky-composer-confirm__subjectrow">
                        <span className="sky-composer-confirm__bodypreview">
                          <b>Body</b> <span data-testid="submission-email-body">{normalizeSubmissionEmailBody(emailBodies[r.funder] ?? '')}</span>
                        </span>
                      </div>
                      {editOpen[r.funder] && (
                        <div className="sky-composer-confirm__editor">
                          <label className="sky-composer-field">
                            <span className="sky-composer-field__label">Subject</span>
                            <input
                              aria-label={`Subject for ${r.funderLabel}`}
                              value={subject}
                              onChange={e => setSubject(e.target.value)}
                              className="sky-composer-input"
                            />
                          </label>
                          <label className="sky-composer-field">
                            <span className="sky-composer-field__label">Body</span>
                            <textarea
                              aria-label={`Body for ${r.funderLabel}`}
                              value={emailBodies[r.funder] ?? SUBMISSION_NUMBER_PLACEHOLDER}
                              onChange={event => setEmailBody(r.funder, event.target.value)}
                              rows={5}
                              className="sky-composer-input sky-composer-confirm__msginput"
                            />
                          </label>
                          <div className="sky-composer-email__hint">
                            Sub # is required and stays first. The actual number is added when submitted. Add your notes below it.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="sky-composer-confirm__email">
            <label className="sky-composer-confirm__sendrow">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={e => setSendEmail(e.target.checked)}
              />
              <span className="sky-composer-confirm__sendlabel">Send email</span>
              <span style={{ flex: 1 }} />
              <span className="sky-composer-email__hint">
                {emailRows.length} funder{emailRows.length === 1 ? '' : 's'} on Submitted
              </span>
            </label>
            {willEmail ? (
              <div className="sky-composer-confirm__emailinfo">
                {selectedDocs.length > 0 && (
                  <>
                    <div className="sky-composer-confirm__attachhead">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                      </svg>
                      {selectedDocs.length} attachment{selectedDocs.length > 1 ? 's' : ''}
                    </div>
                    <div className="sky-composer-confirm__chips">
                      {selectedDocs.map(f => (
                        <span key={f.id} className="sky-composer-confirm__chip" title={f.name}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" /><path d="M14 3v4a2 2 0 0 0 2 2h4" />
                          </svg>
                          <span className="sky-composer-confirm__chipname">{f.name}</span>
                          <button
                            type="button"
                            className="sky-composer-confirm__chipx"
                            onClick={() => onRemoveDoc(f.id)}
                            title={`Remove ${f.name}`}
                            aria-label={`Remove attachment ${f.name}`}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="sky-composer-confirm__noemail">
                {emailRows.length === 0
                  ? 'No Submitted-status rows — these will be added to the table only, no email sent.'
                  : 'Email off — submissions will be added to the table without notifying funders.'}
              </div>
            )}
          </div>
        </div>
        <div className="sky-composer-confirm__actions">
          <button className="sky-composer-clear" onClick={goBack} disabled={submitting}>Go back</button>
          <button className="sky-composer-confirm__go" onClick={() => { saveBodies(); onConfirm(); }} disabled={confirmDisabled}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M20 6 9 17l-5-5" /></svg>
            {submitting
              ? 'Submitting…'
              : recipientPreviewLoading && willEmail
                ? 'Resolving recipients…'
                : willEmail
                  ? 'Confirm & Send'
                  : 'Confirm & Submit'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/* ── Component ──────────────────────────────────────── */

function SubmissionComposer({ merchantId: propMerchantId, merchantName: propMerchantName }: SubmissionComposerProps) {
  const [selectedMerchant, setSelectedMerchant] = useState<{ id: string; label: string } | null>(
    propMerchantId ? { id: String(propMerchantId), label: propMerchantName || String(propMerchantId) } : null
  );
  const merchantId = Number(propMerchantId || selectedMerchant?.id || 0);
  const merchantLabel = propMerchantName || selectedMerchant?.label || '';
  const { data: metadata } = useGlobalMetadata();
  const { session } = useAuth();
  const { token: providerToken, connect: connectGoogle } = useGoogleDriveToken();
  const userId = session?.user?.id;
  const queryClient = useQueryClient();

  const subTableInfo = metadata?.tableData?.byName?.['submission'];
  const subSchema = subTableInfo?.schema_name || 'public';

  const picklist = useCallback((column: string, fallback: string[]): string[] => {
    const values = (metadata as any)?.picklistData?.byTableName?.['submission']?.byFieldName?.[column]?.values;
    if (Array.isArray(values) && values.length) {
      return [...values].sort((a: any, b: any) => (a.place ?? 0) - (b.place ?? 0)).map((v: any) => v.value);
    }
    return fallback;
  }, [metadata]);

  const dealTypes = useMemo(() => picklist('deal_type', FALLBACK_DEAL_TYPES), [picklist]);
  const statuses = useMemo(() => picklist('status', FALLBACK_STATUSES), [picklist]);
  const structures = useMemo(() => picklist('structure', FALLBACK_STRUCTURES), [picklist]);

  const [collapsed, setCollapsed] = useState(true);
  const [rows, setRows] = useState<ComposerRow[]>(() => [freshRow()]);
  const [subject, setSubject] = useState('');
  const [emailBodies, setEmailBodies] = useState<Record<string, string>>({});
  const setEmailBody = (funderId: string, value: string) => setEmailBodies(bodies => ({ ...bodies, [funderId]: value }));
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [recipientPreview, setRecipientPreview] = useState<Record<string, RecipientPreview>>({});
  const [recipientPreviewLoading, setRecipientPreviewLoading] = useState(false);
  const [recipientPreviewError, setRecipientPreviewError] = useState<string | null>(null);
  const [additionalCc, setAdditionalCcState] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  /* Drive documents for the selected merchant */
  const { data: driveFolder } = useMerchantDriveFolder(merchantId);
  const driveQuery = useDriveFiles(driveFolder, null, providerToken);
  const driveFiles = useMemo(
    () => (driveQuery.data ?? []).filter(f => f.mimeType !== DRIVE_FOLDER_MIME_TYPE),
    [driveQuery.data],
  );

  // When the merchant changes: refresh the subject and re-derive the default
  // document selection (statements/application on, voided check / DL off).
  useEffect(() => {
    setSubject(defaultSubject(merchantLabel));
    setAdditionalCcState({});
    setEmailBodies({});
    setRecipientPreview({});
    setRecipientPreviewError(null);
  }, [merchantId, merchantLabel]);

  const checkedInitializedFor = useRef<string>('');
  useEffect(() => {
    const key = `${merchantId}:${driveFiles.map(f => f.id).join(',')}`;
    if (!driveFiles.length || checkedInitializedFor.current === key) return;
    checkedInitializedFor.current = key;
    setChecked(driveFiles.reduce((a, f) => {
      a[f.id] = !UNCHECKED_BY_DEFAULT.test(f.name);
      return a;
    }, {} as Record<string, boolean>));
  }, [merchantId, driveFiles]);

  const setRow = useCallback((key: string, patch: Partial<ComposerRow>) => {
    setRows(rs => rs.map(r => r.key === key ? { ...r, ...patch } : r));
  }, []);

  const addRow = useCallback(() => setRows(rs => [...rs, freshRow()]), []);
  const deleteRow = useCallback((key: string) => {
    setRows(rs => rs.length > 1 ? rs.filter(r => r.key !== key) : rs);
  }, []);
  const clearAll = useCallback(() => {
    setRows([freshRow()]);
    setAdditionalCcState({});
    setEmailBodies({});
    setRecipientPreview({});
    setRecipientPreviewError(null);
  }, []);

  const readyRows = useMemo(() => rows.filter(r => r.funder.trim()), [rows]);
  const takenFunderIds = useMemo(() => rows.map(r => r.funder).filter(Boolean), [rows]);
  const selectedDocs = useMemo(() => driveFiles.filter(f => checked[f.id]), [driveFiles, checked]);
  const previewFunderIds = useMemo(
    () => [...new Set(readyRows.filter(row => row.status === 'Submitted').map(row => Number(row.funder)))],
    [readyRows],
  );
  const previewFunderKey = previewFunderIds.join(',');
  const setAdditionalCc = useCallback((funderId: string, value: string) => {
    setAdditionalCcState(current => ({ ...current, [funderId]: value }));
  }, []);

  useEffect(() => {
    if (!confirmOpen || !sendEmail || !merchantId || !previewFunderIds.length) {
      setRecipientPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setRecipientPreviewLoading(true);
    setRecipientPreviewError(null);
    (async () => {
      try {
        const { data, error } = await supabaseClient.functions.invoke('submission-email', {
          body: { action: 'preview', merchantId, funderIds: previewFunderIds },
        });
        if (cancelled) return;
        if (error) {
          setRecipientPreview({});
          setRecipientPreviewError(error.message || 'Unable to resolve email recipients.');
        } else if (!Array.isArray(data?.results)) {
          setRecipientPreview({});
          setRecipientPreviewError('Unable to resolve email recipients.');
        } else {
          setRecipientPreview(Object.fromEntries(
            (data.results as RecipientPreview[]).map(result => [String(result.funderId), result]),
          ));
        }
      } catch (error) {
        if (cancelled) return;
        setRecipientPreview({});
        setRecipientPreviewError(error instanceof Error ? error.message : 'Unable to resolve email recipients.');
      } finally {
        if (!cancelled) setRecipientPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // The stable key avoids re-running when rows receive unrelated object updates.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmOpen, sendEmail, merchantId, previewFunderKey]);

  useEffect(() => {
    if (!confirmOpen) return;
    workflowBreadcrumb('submission.confirmation.opened', {
      merchantId: String(merchantId ?? ''),
      readyRowCount: readyRows.length,
      submittedTargetCount: previewFunderIds.length,
      documentCount: selectedDocs.length,
      sendEmail,
    });
  }, [confirmOpen, merchantId, previewFunderIds.length, readyRows.length, selectedDocs.length, sendEmail]);

  /* ── Merchant search: multi-field partial match, recents ranked first ── */

  const [merchantState, setMerchantState] = useState<string | null>(null);
  const [merchantIndustry, setMerchantIndustry] = useState<string>('');
  useEffect(() => {
    if (!merchantId) { setMerchantState(null); setMerchantIndustry(''); return; }
    let cancelled = false;
    (async () => {
      const { data } = await supabaseClient
        .from('merchant').select('state, industry').eq('id', merchantId).maybeSingle();
      if (!cancelled) {
        setMerchantState(normalizeState(data?.state ?? null));
        setMerchantIndustry(normalizeIndustry(data?.industry ?? null));
      }
    })();
    return () => { cancelled = true; };
  }, [merchantId]);

  const recentMerchants = useMemo<SearchItem[]>(() => {
    if (propMerchantId) return [];
    return getRecentRecords('merchant', userId).map(r => ({ id: r.id, label: r.label }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, merchantId]); // re-read after each pick

  // Keep ILIKE for these typeahead pickers because they must match partial
  // tokens (for example, "sky" -> "Skyline"). If this becomes a search
  // performance bottleneck, consider a prefix-aware full_text_search or
  // hybrid implementation that preserves the current autocomplete UX.
  const searchMerchants = useCallback(async (term: string): Promise<SearchItem[]> => {
    const safe = term.replace(/[%_,()]/g, ' ').trim();
    if (!safe) return [];
    const pattern = `%${safe}%`;
    const { data, error } = await supabaseClient
      .from('merchant')
      .select('id, business_name, first_name, last_name, phone, email, status')
      .or(['business_name', 'first_name', 'last_name', 'phone', 'email']
        .map(f => `${f}.ilike.${pattern}`).join(','))
      // Most recently active merchants win the 20 slots; without an order the
      // planner picks an arbitrary subset of matches (REG-648).
      .order('updated_at', { ascending: false, nullsFirst: false })
      .limit(20);
    if (error) throw error;
    const rank = recentRankMap('merchant', userId);
    // Unsubmitted merchants (same statuses as the Unsubmitted submenu) are
    // the likeliest submission targets, so they lead; recently viewed and
    // recently active merchants (resubmissions) follow (REG-649).
    const UNSUBMITTED_STATUSES = new Set(['Lead', 'App In', 'App Out']);
    return (data ?? [])
      .map((m: any, index: number) => ({
        id: String(m.id),
        label: m.business_name
          || [m.first_name, m.last_name].filter(Boolean).join(' ')
          || m.phone || m.email || `#${m.id}`,
        unsubmitted: UNSUBMITTED_STATUSES.has(m.status),
        recency: index,
      }))
      .sort((a, b) => Number(b.unsubmitted) - Number(a.unsubmitted)
        || (rank.get(a.id) ?? Infinity) - (rank.get(b.id) ?? Infinity)
        || a.recency - b.recency)
      .map(({ id, label }) => ({ id, label }));
  }, [userId]);

  const pickMerchant = useCallback((id: string, label: string) => {
    setSelectedMerchant(id ? { id, label } : null);
    if (id) {
      pushRecentRecord('merchant', userId, { id, label });
      if (collapsed) setCollapsed(false);
    }
  }, [userId, collapsed]);

  /* ── Client-side Funder search: restrictions + DO-NOT-SUBMIT flags ── */

  const funderItems = useMemo<SearchItem[]>(() => {
    const clientFunders = metadata?.clientSideSelectTables?.funder;
    if (!Array.isArray(clientFunders)) return [];
    return clientFunders.map((f: any) => {
      const id = String(f.id);
      const email = String(f.submission_email ?? '').trim();
      if (takenFunderIds.includes(id)) {
        return { id, label: f.name || `#${id}`, disabled: true, note: 'already added' };
      }
      if (merchantState && parseProhibitedStates(f.prohibited_states).includes(merchantState)) {
        return { id, label: f.name || `#${id}`, disabled: true, note: `won't fund ${merchantState}` };
      }
      const blockedIndustry = industryBlocked(f.prohibited_industries, merchantIndustry);
      if (blockedIndustry) {
        return { id, label: f.name || `#${id}`, disabled: true, note: `won't fund ${blockedIndustry.toLowerCase()}` };
      }
      if (/do\s*not\s*submit/i.test(email)) {
        return { id, label: f.name || `#${id}`, disabled: true, note: 'do not submit' };
      }
      if (f.routing_mode === 'Portal') {
        return { id, label: f.name || `#${id}`, note: 'portal — no auto email' };
      }
      const repEmail = String(f.rep1_email ?? '').trim();
      const toSource = f.routing_mode === 'Rep Email' ? repEmail : (email || repEmail);
      if (!toSource.includes('@')) {
        return { id, label: f.name || `#${id}`, note: 'no submission email' };
      }
      return { id, label: f.name || `#${id}` };
    }).sort((a, b) => a.label.localeCompare(b.label));
  }, [merchantIndustry, merchantState, metadata?.clientSideSelectTables, takenFunderIds]);

  const searchFunders = useCallback(async (term: string): Promise<SearchItem[]> => {
    const normalized = term.trim().toLocaleLowerCase();
    if (!normalized) return funderItems;
    return funderItems.filter((funder) => funder.label.toLocaleLowerCase().includes(normalized));
  }, [funderItems]);

  /** Queues the funder emails for already-created submissions. Mints a fresh
   * Google token per attempt (the mount-time token in state can be an hour
   * stale by the time a rep presses Submit), surfaces the function's real
   * error message, and offers a Retry that reuses the same submissions — so a
   * failed email never requires re-creating rows. */
  const sendSubmissionEmails = useCallback(async (
    payload: Record<string, any>,
    createdCount: number,
  ): Promise<void> => {
    const telemetry = {
      createdCount,
      emailTargetCount: Array.isArray(payload.submissions) ? payload.submissions.length : 0,
      documentCount: Array.isArray(payload.documents) ? payload.documents.length : 0,
      merchantId: String(payload.merchantId ?? ''),
      submissionIds: Array.isArray(payload.submissions)
        ? payload.submissions.map((submission: { submissionId?: number }) => submission.submissionId).filter(Boolean).join(',')
        : '',
      funderIds: Array.isArray(payload.submissions)
        ? payload.submissions.map((submission: { funderId?: number }) => submission.funderId).filter(Boolean).join(',')
        : '',
    };
    try {
      let googleAccessToken: string | null = null;
      if (payload.documents?.length) {
        googleAccessToken = await getGoogleAccessToken(session ?? null);
        if (!googleAccessToken) {
          throw new Error('Google Drive session expired — press "Reconnect Google" in the Documents panel, then Retry.');
        }
      }
      workflowBreadcrumb('submission.email.requested', telemetry);
      const { data: resp, error: fnError } = await traceWorkflow('submission.email.invoke', telemetry, () => supabaseClient.functions.invoke('submission-email', {
        body: { ...payload, googleAccessToken },
      }));
      if (fnError) throw fnError;
      const failed = (resp?.results ?? []).filter((r: any) => r.error);
      if (failed.length) {
        captureWorkflowWarning('submission.email.partial_queue', { ...telemetry, failedCount: failed.length });
        failed.forEach((f: any) => toast.warning(`Email not sent to ${f.funderName || `funder #${f.funderId}`}: ${f.error}`, { duration: 15000 }));
      }
      const queued = resp?.queued ?? 0;
      workflowBreadcrumb('submission.email.response_received', { ...telemetry, queuedCount: queued, failedCount: failed.length });
      toast.success(
        queued
          ? `${createdCount} submission${createdCount > 1 ? 's' : ''} created · emailing ${queued} funder${queued > 1 ? 's' : ''}`
          : `${createdCount} submission${createdCount > 1 ? 's' : ''} created`
      );
      // REG-835: funders with no submission email go to admin as one alert.
      const manual = resp?.manualSend as { funders?: string[]; error?: string } | undefined;
      const manualCount = manual?.funders?.length ?? 0;
      if (manualCount) {
        const funders = `${manualCount} funder${manualCount > 1 ? 's' : ''}`;
        if (manual?.error) {
          toast.warning(`${funders} need${manualCount > 1 ? '' : 's'} a manual send (${manual.funders!.join(', ')}), but the admin email failed: ${manual.error}`, { duration: 20000 });
        } else {
          toast.info(`${funders} need${manualCount > 1 ? '' : 's'} a manual send; admin has been emailed.`, { duration: 10000 });
        }
      }
    } catch (err) {
      captureWorkflowException(err, 'submission.email.invoke', telemetry);
      console.error('submission-email failed:', err);
      const message = await functionErrorMessage(err);
      toast.error(
        `Submissions saved, but the funder emails may not have been sent: ${message} `
        + 'Large batches can outlive the request — Retry is safe: anything already sent is skipped automatically.',
        {
          duration: 30000,
          action: {
            label: 'Retry email',
            onClick: () => { void sendSubmissionEmails(payload, createdCount); },
          },
        },
      );
    }
  }, [session]);

  const submitAll = useCallback(async () => {
    if (!readyRows.length || !merchantId || submitting) return;
    const invalidAdditionalCc = readyRows
      .filter(row => row.status === 'Submitted')
      .flatMap(row => parseCcEmails(additionalCc[row.funder] ?? '').invalid);
    if (invalidAdditionalCc.length) {
      toast.error('Enter valid additional CC email addresses before sending.');
      return;
    }
    setSubmitting(true);

    // Documents ride on the rep's Google token. Verify we can still mint one
    // BEFORE creating anything, so an expired session aborts cleanly instead
    // of leaving submissions saved with no funder email.
    const willEmailDocs = sendEmail
      && selectedDocs.length > 0
      && readyRows.some(r => r.status === 'Submitted');
    if (willEmailDocs) {
      const freshToken = await getGoogleAccessToken(session ?? null);
      if (!freshToken) {
        toast.error('Google Drive session expired — press "Reconnect Google" in the Documents panel, then submit again. Nothing was submitted.', { duration: 20000 });
        setSubmitting(false);
        return;
      }
    }

    const created: { submissionId: number; funderId: number; row: ComposerRow }[] = [];
    const failures: string[] = [];

    for (const r of readyRows) {
      const insertData: Record<string, any> = {
        merchant: merchantId,
        funder: Number(r.funder),
        deal_type: r.dealType,
        status: r.status,
      };
      const amtN = parseMoney(r.amount);
      if (amtN) insertData.amount = amtN;
      const facN = parseFactor(r.factor);
      if (facN) insertData.factor_rate = facN;
      const termN = parseTerm(r.term);
      if (termN) insertData.term = termN;
      if (r.commPct) {
        const pct = parseFloat(r.commPct.replace('%', ''));
        if (!isNaN(pct)) insertData.commission_percent = pct;
      }
      if (r.approvalDate) insertData.approval_date = r.approvalDate;
      if (r.structure) insertData.structure = r.structure;
      if (r.status === 'Declined' && r.declineReason.trim()) insertData.reason_for_decline = r.declineReason.trim();
      if (r.status === 'Missing documents' && r.missingDocs.trim()) insertData.missing_documents = r.missingDocs.trim();

      const { data, error } = await insertRecord({
        tableName: 'submission',
        schema: subSchema,
        data: insertData,
      });
      if (error || !data?.[0]?.id) {
        console.error('Failed to create submission:', error);
        failures.push(r.funderLabel || 'submission');
      } else {
        created.push({ submissionId: data[0].id as number, funderId: Number(r.funder), row: r });
      }
    }

    if (failures.length) {
      toast.error(`Failed to create: ${failures.join(', ')}`);
    }

    if (created.length) {
      queryClient.invalidateQueries({ queryKey: ['submission'] });
      // The Submissions list and its submenus are report grids (REG-828): re-read them in place.
      requestReportGridRefresh();

      const emailTargets = created.filter(c => c.row.status === 'Submitted');
      if (sendEmail && emailTargets.length) {
        await sendSubmissionEmails({
          action: 'send',
          merchantId,
          subject: subject.trim() || defaultSubject(merchantLabel),
          submissions: emailTargets.map(c => ({
            submissionId: c.submissionId,
            funderId: c.funderId,
            emailBody: normalizeSubmissionEmailBody(emailBodies[String(c.funderId)] ?? ''),
            ccEmails: parseCcEmails(additionalCc[String(c.funderId)] ?? '').emails,
          })),
          documents: selectedDocs.map(f => ({ id: f.id, name: f.name, mimeType: f.mimeType })),
        }, created.length);
      } else {
        toast.success(`${created.length} submission${created.length > 1 ? 's' : ''} created`);
      }
      clearAll();
      setConfirmOpen(false);
    }
    setSubmitting(false);
  }, [readyRows, merchantId, merchantLabel, submitting, additionalCc, subSchema, queryClient, sendEmail, subject, emailBodies, selectedDocs, session, sendSubmissionEmails, clearAll]);

  return (
    <div className="sky-composer">
      {/* Header */}
      <div className="sky-composer-head" onClick={() => setCollapsed(c => !c)}>
        <div className="sky-composer-icon">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" x2="12" y1="3" y2="15" />
          </svg>
        </div>
        <div className="sky-composer-title">
          <span className="sky-composer-title__main">New Submissions</span>
          <span className="sky-composer-title__sub">Submit deals to funders, or log approvals & declines</span>
        </div>
        <div className="sky-composer-merchant" onClick={e => e.stopPropagation()}>
          <span className="sky-composer-merchant__label">For</span>
          {propMerchantId ? (
            <span className="sky-composer-merchant__name">{merchantLabel}</span>
          ) : (
            <ServerSearchSelect
              value={selectedMerchant?.id || ''}
              displayValue={selectedMerchant?.label || ''}
              placeholder="Search merchant…"
              className="sky-searchsel--merchant"
              triggerClassName="sky-composer-merchant__input"
              minWidth={260}
              onSearch={searchMerchants}
              emptyItems={recentMerchants}
              emptyItemsLabel="Recently used"
              onChange={pickMerchant}
            />
          )}
        </div>
        <div style={{ flex: 1 }} />
        {readyRows.length > 0 && (
          <span className="sky-composer-ready">{readyRows.length} ready</span>
        )}
        <button
          className="sky-composer-toggle"
          onClick={(e) => { e.stopPropagation(); setCollapsed(c => !c); }}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          <svg
            width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
            style={{ transition: 'transform 0.15s ease', transform: collapsed ? 'none' : 'rotate(180deg)' }}
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
      </div>

      {!collapsed && (
        <>
          <div className={`sky-composer-body ${merchantId ? 'sky-composer-body--split' : ''}`}>
            {/* LEFT — submission rows */}
            <div className="sky-composer-rows">
              {rows.map((r, i) => (
                <DealRow
                  key={r.key}
                  r={r}
                  first={i === 0}
                  dealTypes={dealTypes}
                  statuses={statuses}
                  structures={structures}
                  funderItems={funderItems}
                  onSearchFunders={searchFunders}
                  onSet={patch => setRow(r.key, patch)}
                  onDelete={() => deleteRow(r.key)}
                  canDelete={rows.length > 1}
                />
              ))}
            </div>

            {/* RIGHT — email (only once a merchant is set) */}
            {merchantId > 0 && (
              <EmailPanel
                subject={subject}
                setSubject={setSubject}
                checked={checked}
                setChecked={setChecked}
                files={driveFiles}
                filesLoading={driveQuery.isLoading}
                filesError={driveQuery.error ? (driveQuery.error as Error).message : null}
                hasDriveFolder={!!driveFolder}
                driveFolderUrl={canonicalDriveFolderUrl(driveFolder)}
                providerToken={providerToken}
                onConnectGoogle={connectGoogle}
              />
            )}
          </div>

          {/* Footer */}
          <div className="sky-composer-footer">
            <button className="sky-composer-add" onClick={addRow}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>+</span> Add submission
            </button>
            <div style={{ flex: 1 }} />
            <button className="sky-composer-clear" onClick={clearAll}>Clear</button>
            <button
              className="sky-composer-submit"
              onClick={() => { if (merchantId && readyRows.length) setConfirmOpen(true); }}
              disabled={!readyRows.length || !merchantId || submitting}
              title={!merchantId ? 'Pick a merchant first' : undefined}
            >
              Submit {readyRows.length || ''}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        </>
      )}

      {confirmOpen && (
        <ConfirmDialog
          merchantLabel={merchantLabel}
          rows={readyRows}
          subject={subject}
          setSubject={setSubject}
          emailBodies={emailBodies}
          setEmailBody={setEmailBody}
          recipientPreview={recipientPreview}
          recipientPreviewLoading={recipientPreviewLoading}
          recipientPreviewError={recipientPreviewError}
          additionalCc={additionalCc}
          setAdditionalCc={setAdditionalCc}
          selectedDocs={selectedDocs}
          onRemoveDoc={id => setChecked(c => ({ ...c, [id]: false }))}
          sendEmail={sendEmail}
          setSendEmail={setSendEmail}
          submitting={submitting}
          onBack={() => { if (!submitting) setConfirmOpen(false); }}
          onConfirm={submitAll}
        />
      )}
    </div>
  );
}

/* ── Deal Row ───────────────────────────────────────── */

interface DealRowProps {
  r: ComposerRow;
  first: boolean;
  dealTypes: string[];
  statuses: string[];
  structures: string[];
  funderItems: SearchItem[];
  onSearchFunders: (term: string) => Promise<SearchItem[]>;
  onSet: (patch: Partial<ComposerRow>) => void;
  onDelete: () => void;
  canDelete: boolean;
}

function DealRow({
  r, first, dealTypes, statuses, structures,
  funderItems, onSearchFunders, onSet, onDelete, canDelete,
}: DealRowProps) {
  const amtN = parseMoney(r.amount);
  const facN = parseFactor(r.factor);
  const termN = parseTerm(r.term);
  const commN = r.commPct ? parseFloat(r.commPct.replace('%', '')) : 0;
  const payback = amtN && facN ? fmtMoney(amtN * facN) : '';
  const payment = amtN && facN && termN ? fmtMoney((amtN * facN) / termN) : '';
  const commDollar = amtN && commN && !isNaN(commN) ? fmtMoney(amtN * commN / 100) : '';
  const tint = statusTint(r.status);

  const statusLabels = useMemo(
    () => Object.fromEntries(statuses.map(s => [s, statusLabel(s)])),
    [statuses],
  );

  return (
    <div className={`sky-composer-deal ${first ? '' : 'sky-composer-deal--nth'}`}>
      <div className="sky-composer-deal__grid">
        <Field
          label="Deal Type"
          value={r.dealType}
          onChange={v => onSet({ dealType: v })}
          options={dealTypes}
        />
        <label className="sky-composer-field">
          <span className="sky-composer-field__label">Funder</span>
          <ServerSearchSelect
            value={r.funder}
            displayValue={r.funderLabel}
            placeholder="Funder…"
            onSearch={onSearchFunders}
            emptyItems={funderItems}
            emptyItemsLabel="Funders"
            onChange={(id, label) => onSet({ funder: id, funderLabel: label })}
          />
        </label>
        <Field
          label="Status"
          value={r.status}
          onChange={v => onSet({ status: v })}
          options={statuses}
          optionLabels={statusLabels}
          inputStyle={{
            fontWeight: 600,
            color: tint.fg === '#fff' ? '#111827' : tint.fg,
            background: tint.bg === '#1f2328' ? '#fff' : tint.bg,
            borderColor: tint.bd,
          }}
        />
        <div className="sky-composer-deal__btns">
          <button
            className={`sky-composer-deal__expand ${r.expanded ? 'sky-composer-deal__expand--open' : ''}`}
            onClick={() => onSet({ expanded: !r.expanded })}
            title="Approval details"
          >
            <svg
              width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              style={{ transition: 'transform 0.15s ease', transform: r.expanded ? 'rotate(180deg)' : 'none' }}
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <button
            className="sky-composer-row-delete"
            onClick={onDelete}
            disabled={!canDelete}
            title="Remove row"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
              <path d="M10 11v6M14 11v6" />
            </svg>
          </button>
        </div>
      </div>

      {r.status !== 'Submitted' && (
        <div className="sky-composer-deal__notice">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" />
          </svg>
          {statusLabel(r.status)} is logged to the table only — no email is sent.
        </div>
      )}

      {r.expanded && (
        <div className="sky-composer-deal__details">
          <div className="sky-composer-deal__detailgrid">
            <Field label="Amount" value={r.amount} onChange={v => onSet({ amount: v })} placeholder="$0" mono />
            <Field label="Factor" value={r.factor} onChange={v => onSet({ factor: v })} placeholder="1.40" mono />
            <Field label="Term" value={r.term} onChange={v => onSet({ term: v })} placeholder="24 wks" mono />
            <Field label="Comm %" value={r.commPct} onChange={v => onSet({ commPct: v })} placeholder="10%" mono />
            <Field label="Approval Date" value={r.approvalDate} onChange={v => onSet({ approvalDate: v })} type="date" />
            <Field label="Structure" value={r.structure} onChange={v => onSet({ structure: v })} options={structures} />
            <Field label="Payback" value={payback} readOnly mono />
            <Field label="Payment" value={payment} readOnly mono />
          </div>
          {commDollar && (
            <div className="sky-composer-deal__comm">
              Commission <b>{commDollar}</b>
            </div>
          )}
          {r.status === 'Declined' && (
            <Field label="Decline Reason" value={r.declineReason} onChange={v => onSet({ declineReason: v })} placeholder="e.g. Overleveraged" />
          )}
          {r.status === 'Missing documents' && (
            <Field label="Missing Document" value={r.missingDocs} onChange={v => onSet({ missingDocs: v })} placeholder="e.g. June MTD" />
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(SubmissionComposer);
